import { BrightnessControl } from "@/components/coverage-beta/BrightnessControl";
import { ReferenceImage } from "@/components/coverage-beta/ReferenceImage";
import { ReferenceImageThumbnail } from "@/components/coverage-beta/ReferenceImageThumbnail";
import {
  SampleOosPrediction,
  ReferenceImageMatch,
} from "@/dev/sample-out-of-stock-predictions";
import { useFetchGalleryImgUrl } from "@/hooks/useFetchGalleryImgUrl";
import {
  selectActiveAreaIdx,
  useCoverageBetaStore,
} from "@/store/useCoverageBetaStore";
import { cn } from "@/utils/utils";
import { OoSParcelSchema } from "@augmodo/lebron-browser-client";

interface Props {
  out_of_stock_predictions: SampleOosPrediction[];
  parcelResp: OoSParcelSchema | undefined;
  /** Forwarded to every ReferenceImage — see its `onVisibleLoad` prop. */
  onActiveReferenceLoaded: () => void;
}

export const ShelfImagesView = (props: Props) => {
  const { out_of_stock_predictions, parcelResp, onActiveReferenceLoaded } =
    props;

  const references = parcelResp?.references ?? [];

  // Query image is already fetched by the query-image panel; this hits the
  // React Query cache (keyed by image_id) so no extra request is made. We only
  // need its capture time to compute each shelf image's relative age.
  const { data: queryImgResp } = useFetchGalleryImgUrl(
    parcelResp?.query?.image_id ?? "",
  );
  const queryTimestamp = queryImgResp?.timestamp;

  const activeRefImgIdx = useCoverageBetaStore((s) => s.activeRefImgIdx);
  // In QA mode this follows the pager (-1 for a person item — matches no
  // empty_area_index, so no reference-image match overlay shows) rather than
  // the frozen draw/edit-flow index. See selectActiveAreaIdx's doc comment.
  const activeAreaIdx = useCoverageBetaStore(selectActiveAreaIdx);
  const setHoveredView = useCoverageBetaStore((s) => s.setHoveredView);

  const refIdxToReferenceImageMatches = out_of_stock_predictions.reduce<
    Record<number, ReferenceImageMatch[]>
  >((acc, prediction) => {
    const { empty_area_index, matches_products_reference_images } = prediction;
    if (empty_area_index !== activeAreaIdx) return acc;

    matches_products_reference_images.forEach((match) => {
      acc[match.reference_image_index] ??= [];
      acc[match.reference_image_index].push(match);
    });

    return acc;
  }, {});

  return (
    <div
      className="relative h-full"
      onMouseEnter={() => setHoveredView("reference")}
      data-cb-tour="shelf-images-view"
    >
      {/* Reference-image brightness — mirrors the query toolbar's control so both
          views have an identical slider + the shared F/G/H hotkeys. */}
      <div className="absolute bottom-2 right-2 z-10">
        <BrightnessControl view="reference" position="top-end" />
      </div>

      <div className="absolute top-2 left-2 z-10 ">
        <div className="flex gap-2">
          {references.map((reference, refIdx) => {
            const isActive = refIdx === activeRefImgIdx;
            const refImageMatches =
              refIdxToReferenceImageMatches?.[refIdx] ?? [];
            return (
              <ReferenceImageThumbnail
                key={reference.context_id}
                reference={reference}
                isActive={isActive}
                refIdx={refIdx}
                refImageMatches={refImageMatches}
              />
            );
          })}
        </div>
      </div>

      {references.map((reference, referenceImgIdx) => {
        const isVisible = referenceImgIdx === activeRefImgIdx;
        const refImageMatches =
          refIdxToReferenceImageMatches?.[referenceImgIdx] ?? [];

        return (
          <div
            key={reference.context_id}
            className={cn("h-full", isVisible ? "block" : "hidden")}
          >
            <ReferenceImage
              reference={reference}
              refImageMatches={refImageMatches}
              isVisible={isVisible}
              referenceImgIdx={referenceImgIdx}
              queryTimestamp={queryTimestamp}
              onVisibleLoad={onActiveReferenceLoaded}
            />
          </div>
        );
      })}
    </div>
  );
};
